import { Colli } from '@frontend/colli/types';
import { SlotRendering } from '@frontend/common-types';
import { ModuleClient } from '@frontend/module/api';
import { Module } from '@frontend/module/types';
import { PackageWorkflowClient } from '@frontend/package/api';
import { Package, PackageTriggerName, PackageType } from '@frontend/package/types';
import { SlotClient } from '@frontend/slot/api';
import { Slot, SlotType } from '@frontend/slot/types';
import { ScreenTrigger, ScreenType, Step } from '@frontend/wizard/types';
import React, { useEffect, useMemo, useState } from 'react';

import { SlotLayoutCell, slotLayoutDisplayNumber } from '../../components/slot-layout-cell';
import { useScreen } from '../screens/useScreen';
import {
    COMPLETED_OPEN_SLOT_PACKAGE_IDS_KEY,
    SERIAL_CONFIRM_OPEN_SLOTS_SCREEN_KEY,
    SerialProgress,
    getCompletedOpenSlotPackageIds,
    getNextSerialPackage,
    getSeededPackagesFromWizardData,
    getSerialProgress,
    resolveOpenablePackagesForCollis
} from '../utils/colli-open-slot-context';
import { useWizardPackage } from './useWizardPackage';
import { useWizardTransaction } from './useWizardTransaction';

export interface ColliConfirmOpenSlotsSerialViewProps {
    accountId?: string;
    spotId?: string;
    currentModule: Module | null;
    modules: Module[] | null;
    selectedSlotId: string | null;
    progress: SerialProgress;
    resolveError: boolean;
    setCurrentModule: (module: Module | null) => void;
    slotComponent: (slotRendering: SlotRendering) => React.ReactNode;
    submitDisabled: boolean;
    onSubmit: () => void;
    onBack: (screenType: ScreenType) => void;
    selectModuleModalOpen: boolean;
    setSelectModuleModalOpen: (open: boolean) => void;
}

interface UseColliConfirmOpenSlotsSerialArgs {
    screenType: ScreenType;
    packageType: PackageType.DROP_OFF | PackageType.PICK_UP;
}

export function useColliConfirmOpenSlotsSerial(args: UseColliConfirmOpenSlotsSerialArgs): ColliConfirmOpenSlotsSerialViewProps {
    const { navigationState, store, updateWizardData, onBack, onSubmit: onContinue } = useScreen();
    const { transaction } = useWizardTransaction({ wizardId: store!.wizard.id, store });
    const { localUpdatePackage } = useWizardPackage({ wizardId: store!.wizard.id, store });

    const [modules, setModules] = useState<Module[] | null>(null);
    const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
    const [allSlots, setAllSlots] = useState<Slot[] | null>(null);
    const [packages, setPackages] = useState<Package[]>([]);
    const [currentPackage, setCurrentPackage] = useState<Package | null>(null);
    const [resolveError, setResolveError] = useState(false);
    const [selectModuleModalOpen, setSelectModuleModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);

    const collis = useMemo(() => (store?.data.collis as Colli[] | undefined) ?? [], [store?.data.collis]);
    const completedIds = useMemo(() => getCompletedOpenSlotPackageIds(store?.data as Record<string, unknown>), [store?.data]);
    const progress = useMemo(() => getSerialProgress(packages, completedIds, currentPackage, collis), [packages, completedIds, currentPackage, collis]);
    const selectedSlotId = currentPackage?.slot_id ?? null;

    useEffect(() => {
        const accountId = navigationState.iot?.account_id || transaction?.account_id;
        const spotId = navigationState.iot?.spot_id || transaction?.spot_id;
        if (!accountId || !spotId) return;

        ModuleClient.fetchModules({ spot_id: spotId }).then((result) => {
            const sorted = result.results.sort((a, b) => (a.index || '').localeCompare(b.index || '', undefined, { numeric: true, sensitivity: 'base' }));
            setModules(sorted);
        });
        SlotClient.fetchSlots({ spot_id: spotId, size: '1000', index: '0' }).then((result) => setAllSlots(result.results));
    }, []);

    useEffect(() => {
        const accountId = navigationState.iot?.account_id || transaction?.account_id || (store?.data.package as Package | undefined)?.account_id;
        const transactionId = transaction?.id || (store?.data.package as Package | undefined)?.transaction_id;
        if (!accountId || !transactionId) return;

        let cancelled = false;
        setLoading(true);
        setResolveError(false);

        resolveOpenablePackagesForCollis({
            accountId,
            transactionId,
            spotId: navigationState.spot?.id ?? navigationState.iot?.spot_id,
            collis,
            type: args.packageType,
            seededPackages: getSeededPackagesFromWizardData(store?.data as Record<string, unknown>)
        })
            .then((resolved) => {
                if (cancelled) return;
                setPackages(resolved);
                const next = getNextSerialPackage(resolved, getCompletedOpenSlotPackageIds(store?.data as Record<string, unknown>));
                setCurrentPackage(next);
                if (!next) {
                    setResolveError(true);
                    return;
                }
                updateWizardData({ packages: resolved, package: next });
                localUpdatePackage(next);
            })
            .catch(() => {
                if (!cancelled) setResolveError(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [transaction?.id, args.packageType, collis, completedIds.join(','), store?.data.package?.id]);

    useEffect(() => {
        if (!modules || !currentPackage?.module_id) return;
        const idx = modules.findIndex((m) => m.id === currentPackage.module_id);
        if (idx >= 0) setCurrentModuleIndex(idx);
    }, [modules, currentPackage]);

    const currentModule = modules?.[currentModuleIndex] ?? null;

    const setCurrentModule = (module: Module | null) => {
        if (modules && module) {
            const index = modules.findIndex((m) => m.id === module.id);
            if (index >= 0) setCurrentModuleIndex(index);
        }
        setSelectModuleModalOpen(false);
    };

    const resolveNextStep = (): Step | undefined => store?.steps.find((step) => step.screen === args.screenType && step.trigger === ScreenTrigger.SUBMIT);

    const onSubmit = async () => {
        if (!store || !currentPackage) return;

        const nextCompleted = [...new Set([...completedIds, currentPackage.id])];
        updateWizardData({
            packages,
            package: currentPackage,
            [COMPLETED_OPEN_SLOT_PACKAGE_IDS_KEY]: nextCompleted,
            [SERIAL_CONFIRM_OPEN_SLOTS_SCREEN_KEY]: args.screenType
        });

        await PackageWorkflowClient.triggerPackageState(currentPackage.account_id, currentPackage.transaction_id, currentPackage.id, {
            trigger: PackageTriggerName.PACKAGE_OPEN_SLOT,
            location: navigationState.spot?.id ?? undefined,
            actor: navigationState.user?.id ?? undefined
        }).finally(() => {
            onContinue(resolveNextStep());
        });
    };

    const slotComponent = (slotRendering: SlotRendering): React.ReactNode => {
        const slot = allSlots?.find((s) => s.id === slotRendering.id) ?? null;
        const isHighlighted = !!slot?.id && slot.id === selectedSlotId;

        return (
            <SlotLayoutCell
                slotNumber={slotLayoutDisplayNumber(slot)}
                isSelected={isHighlighted}
                isDisabled={slot?.type === SlotType.TERMINAL}
                hasContent={false}
                onSelect={isHighlighted ? onSubmit : undefined}
            />
        );
    };

    return {
        accountId: navigationState.iot?.account_id || transaction?.account_id || undefined,
        spotId: navigationState.iot?.spot_id || transaction?.spot_id || undefined,
        currentModule,
        modules,
        selectedSlotId,
        progress,
        resolveError: resolveError || (!loading && !currentPackage),
        setCurrentModule,
        slotComponent,
        submitDisabled: loading || !currentPackage || !selectedSlotId,
        onSubmit,
        onBack,
        selectModuleModalOpen,
        setSelectModuleModalOpen
    };
}
